import { Info } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import Modal from "@/components/ui/Modal";
import type { Installment } from "@/types/installment";
import { formatDueDate, formatINR } from "../data";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toDisplay(d: Date): string {
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function twoDaysFromToday(): string {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return toDisplay(d);
}

interface RetryModalProps {
  open: boolean;
  onClose: () => void;
  installment: Installment | null;
  onConfirm: (retryDate: string) => void;
  loading?: boolean;
}

export default function RetryModal({ open, onClose, installment, onConfirm, loading }: RetryModalProps) {
  const [retryDate, setRetryDate] = useState(twoDaysFromToday());
  const [error, setError] = useState("");

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  function handleConfirm() {
    const parsed = new Date(retryDate);
    if (!retryDate || isNaN(parsed.getTime()) || parsed.getTime() < todayStart.getTime()) {
      setError("Select a retry date that is not in the past");
      return;
    }
    onConfirm(retryDate);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Retry this installment?"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            Schedule Retry
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-[#BAE6FD] bg-[#F0F9FF] p-3.5">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-info" />
        <p className="text-[13px] text-ink">
          The bank will be asked to present this installment again on the date you select. There is no additional
          charge for a retry.
        </p>
      </div>

      <div className="mt-4 rounded-lg border border-line bg-page p-4">
        <p className="text-sm font-semibold text-ink">Installment</p>
        <div className="mt-2 space-y-2.5">
          {[
            {
              label: "Installment Number",
              value: installment ? `${installment.installmentNumber} of ${installment.totalInstallments}` : "—",
            },
            { label: "Amount", value: installment ? formatINR(installment.amount) : "—" },
            { label: "Original Due Date", value: installment ? formatDueDate(installment.dueDate) : "—" },
            { label: "Failure Reason", value: installment?.failureReason ?? "—" },
          ].map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-4">
              <span className="text-[13px] text-ink-secondary">{r.label}</span>
              <span className="text-right text-sm font-medium text-ink">{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <DatePicker
          label="Retry Date"
          required
          value={retryDate}
          onChange={(v) => {
            setRetryDate(v);
            if (v) setError("");
          }}
          error={error}
        />
        <p className="mt-1.5 text-xs text-ink-secondary">
          Select the date on which the bank should re-present this installment.
        </p>
      </div>
    </Modal>
  );
}